import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "@apollo/client";
import {
  Card,
  Button,
  Form,
  Modal,
  Pagination,
  Table,
  Row,
  Col,
  Badge,
  Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import PageHeader from "../theme/components/common/PageHeader";
import SessionTimeout from "../components/common/SessionTimeout";
import { SearchableDropdown } from "../components/common/SearchableDropdown";
import { GET_ORGANIZATIONS } from "../utils/users";
import { GET_MEASUREMENT_STATIONS } from "../utils/stations";
import { gettext } from "../i18n";
import apiClient from "../api";
import "./StationManagementPage.scss";

interface DataSource {
  id: number;
  type: "channel" | "formula" | "manual_point_set";
  name: string;
  source_uuid: string;
  unit_symbol?: string | null;
  datapoint_count?: number;
  expression?: string;
}

interface Station {
  id: number;
  identifier: string;
  name: string;
  organization_name?: string | null;
  channels: DataSource[];
  formulas: DataSource[];
  manual_point_sets: DataSource[];
}

interface StationsResponse {
  stations: Station[];
  total: number;
}

const ITEMS_PER_PAGE = 20;

const DataSourceTypeBadge = ({ type }: { type: string }) => {
  const variant =
    type === "channel" ? "primary" : type === "formula" ? "info" : "warning";
  const label =
    type === "channel"
      ? gettext("Channel")
      : type === "formula"
        ? gettext("Formula")
        : gettext("Manual");
  return <Badge bg={variant}>{label}</Badge>;
};

const StationManagementPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedStations, setExpandedStations] = useState<Set<number>>(
    new Set(),
  );
  const [selectedStations, setSelectedStations] = useState<Set<number>>(
    new Set(),
  );
  const [selectedDataSources, setSelectedDataSources] = useState<
    Map<number, Set<string>>
  >(new Map());
  const [loading, setLoading] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteStationModal, setShowDeleteStationModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showDeleteDatasourceModal, setShowDeleteDatasourceModal] =
    useState(false);

  const [newStationIdentifier, setNewStationIdentifier] = useState("");
  const [newStationName, setNewStationName] = useState("");
  const [newStationOrgId, setNewStationOrgId] = useState<number | "">("");

  const [targetStationId, setTargetStationId] = useState<number | "">("");
  const [targetDataSourceId, setTargetDataSourceId] = useState<number | "">("");

  const debouncedSetQuery = useMemo(
    () => debounce((value: string) => setDebouncedSearchQuery(value), 400),
    [],
  );

  React.useEffect(() => {
    debouncedSetQuery(searchQuery);
    return () => debouncedSetQuery.cancel();
  }, [searchQuery, debouncedSetQuery]);

  const { data: organizationsData } = useQuery(GET_ORGANIZATIONS);
  const { data: allStationsData } = useQuery(GET_MEASUREMENT_STATIONS);

  const [stationsData, setStationsData] = useState<StationsResponse | null>(
    null,
  );
  const [fetchError, setFetchError] = useState<Error | null>(null);

  const fetchStations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.request(
        `/admin/list-stations-with-datasources?search=${encodeURIComponent(
          debouncedSearchQuery,
        )}&limit=${ITEMS_PER_PAGE}&offset=${(currentPage - 1) * ITEMS_PER_PAGE}`,
        { method: "GET" },
      );
      setStationsData(response as StationsResponse);
      setFetchError(null);
    } catch (e: any) {
      setFetchError(e);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchQuery, currentPage]);

  React.useEffect(() => {
    fetchStations();
  }, [fetchStations]);

  const toggleStationExpanded = (stationId: number) => {
    setExpandedStations((prev) => {
      const next = new Set(prev);
      if (next.has(stationId)) {
        next.delete(stationId);
      } else {
        next.add(stationId);
      }
      return next;
    });
  };

  const toggleStationSelected = (stationId: number) => {
    setSelectedStations((prev) => {
      const next = new Set(prev);
      if (next.has(stationId)) {
        next.delete(stationId);
      } else {
        next.add(stationId);
        setExpandedStations(new Set());
      }
      return next;
    });
  };

  const toggleDataSourceSelected = (
    stationId: number,
    dataSourceKey: string,
  ) => {
    setSelectedDataSources((prev) => {
      const next = new Map(prev);
      const stationSelections = new Set(next.get(stationId) || []);
      if (stationSelections.has(dataSourceKey)) {
        stationSelections.delete(dataSourceKey);
      } else {
        stationSelections.add(dataSourceKey);
      }
      if (stationSelections.size === 0) {
        next.delete(stationId);
      } else {
        next.set(stationId, stationSelections);
      }
      return next;
    });
  };

  const getAllDataSourceKeysForStation = (station: Station): string[] => {
    const keys: string[] = [];
    station.channels.forEach((ds) => keys.push(`channel:${ds.id}`));
    station.formulas.forEach((ds) => keys.push(`formula:${ds.id}`));
    station.manual_point_sets.forEach((ds) =>
      keys.push(`manual_point_set:${ds.id}`),
    );
    return keys;
  };

  const areAllDataSourcesSelected = (station: Station): boolean => {
    const allKeys = getAllDataSourceKeysForStation(station);
    if (allKeys.length === 0) return false;
    const stationSelections = selectedDataSources.get(station.id) || new Set();
    return allKeys.every((key) => stationSelections.has(key));
  };

  const toggleSelectAllDataSources = (station: Station) => {
    const allKeys = getAllDataSourceKeysForStation(station);
    const stationSelections = selectedDataSources.get(station.id) || new Set();
    const allSelected = allKeys.every((key) => stationSelections.has(key));

    setSelectedDataSources((prev) => {
      const next = new Map(prev);
      if (allSelected) {
        next.delete(station.id);
      } else {
        next.set(station.id, new Set(allKeys));
      }
      return next;
    });
  };

  const getAllSelectedDataSources = (): Array<{
    stationId: number;
    id: number;
    type: string;
    name: string;
  }> => {
    const result: Array<{
      stationId: number;
      id: number;
      type: string;
      name: string;
    }> = [];
    selectedDataSources.forEach((selections, stationId) => {
      selections.forEach((key) => {
        const [type, idStr] = key.split(":");
        const id = parseInt(idStr, 10);
        let name = "";
        const station = stationsData?.stations.find((s) => s.id === stationId);
        if (station) {
          if (type === "channel") {
            const ds = station.channels.find((c) => c.id === id);
            if (ds) name = ds.name;
          } else if (type === "formula") {
            const ds = station.formulas.find((f) => f.id === id);
            if (ds) name = ds.name;
          } else if (type === "manual_point_set") {
            const ds = station.manual_point_sets.find((m) => m.id === id);
            if (ds) name = ds.name;
          }
        }
        result.push({ stationId, id, type, name });
      });
    });
    return result;
  };

  const getSelectedDataSourceTypes = (): Set<string> => {
    const types = new Set<string>();
    getAllSelectedDataSources().forEach((ds) => types.add(ds.type));
    return types;
  };

  const canConvert = (): boolean => {
    const types = getSelectedDataSourceTypes();
    const allSelected = getAllSelectedDataSources();
    return (
      allSelected.length > 0 &&
      types.size === 1 &&
      (types.has("channel") || types.has("manual_point_set"))
    );
  };

  const canMerge = (): boolean => {
    const types = getSelectedDataSourceTypes();
    const allSelected = getAllSelectedDataSources();
    return (
      allSelected.length >= 2 &&
      types.size === 1 &&
      (types.has("channel") || types.has("manual_point_set"))
    );
  };

  const handleCreateStation = async () => {
    if (!newStationIdentifier || !newStationName) {
      toast.error(gettext("Identifier and name are required"));
      return;
    }
    try {
      await apiClient.request("/admin/create-station", {
        method: "POST",
        data: {
          identifier: newStationIdentifier,
          name: newStationName,
          organization_id: newStationOrgId || null,
        },
      });
      toast.success(gettext("Station created successfully"));
      setShowCreateModal(false);
      setNewStationIdentifier("");
      setNewStationName("");
      setNewStationOrgId("");
      fetchStations();
    } catch (e: any) {
      toast.error(
        `${gettext("Error creating station")}: ${
          e.message?.toString() || "Unknown error"
        }`,
      );
    }
  };

  const handleDeleteStation = async () => {
    if (selectedStations.size !== 1) return;
    const stationId = Array.from(selectedStations)[0];
    try {
      await apiClient.request("/admin/delete-station", {
        method: "POST",
        data: { station_id: stationId },
      });
      toast.success(gettext("Station deleted successfully"));
      setShowDeleteStationModal(false);
      setSelectedStations(new Set());
      fetchStations();
    } catch (e: any) {
      toast.error(
        `${gettext("Error deleting station")}: ${
          e.message?.toString() || "Unknown error"
        }`,
      );
    }
  };

  const handleMoveDataSources = async () => {
    if (!targetStationId) {
      toast.error(gettext("Please select a target station"));
      return;
    }
    const selected = getAllSelectedDataSources();
    if (selected.length === 0) return;

    try {
      const sourceType = selected[0].type;
      await apiClient.request("/admin/move-datasources", {
        method: "POST",
        data: {
          source_ids: selected.map((ds) => ds.id),
          source_type: sourceType,
          target_station_id: targetStationId,
        },
      });
      toast.success(gettext("Data sources moved successfully"));
      setShowMoveModal(false);
      setSelectedDataSources(new Map());
      setTargetStationId("");
      fetchStations();
    } catch (e: any) {
      toast.error(
        `${gettext("Error moving data sources")}: ${
          e.message?.toString() || "Unknown error"
        }`,
      );
    }
  };

  const handleMergeDataSources = async () => {
    if (!targetDataSourceId) {
      toast.error(gettext("Please select a target data source"));
      return;
    }
    const selected = getAllSelectedDataSources();
    if (selected.length < 2 || isMerging) return;

    setIsMerging(true);
    try {
      const sourceType = selected[0].type;
      await apiClient.request("/admin/merge-datasources", {
        method: "POST",
        data: {
          source_ids: selected.map((ds) => ds.id),
          source_type: sourceType,
          target_id: targetDataSourceId,
        },
      });
      toast.success(gettext("Data sources merged successfully"));
      setShowMergeModal(false);
      setSelectedDataSources(new Map());
      setTargetDataSourceId("");
      fetchStations();
    } catch (e: any) {
      toast.error(
        `${gettext("Error merging data sources")}: ${
          e.message?.toString() || "Unknown error"
        }`,
      );
    } finally {
      setIsMerging(false);
    }
  };

  const handleConvert = async () => {
    const selected = getAllSelectedDataSources();
    if (selected.length === 0) return;

    const type = selected[0].type;
    const id = selected[0].id;

    try {
      if (type === "channel") {
        await apiClient.request("/admin/convert-channel-to-manual", {
          method: "POST",
          data: { channel_id: id },
        });
        toast.success(gettext("Channel converted to manual point set"));
      } else if (type === "manual_point_set") {
        await apiClient.request("/admin/convert-manual-to-channel", {
          method: "POST",
          data: { manual_point_set_id: id },
        });
        toast.success(gettext("Manual point set converted to channel"));
      }
      setShowConvertModal(false);
      setSelectedDataSources(new Map());
      fetchStations();
    } catch (e: any) {
      toast.error(
        `${gettext("Error converting data source")}: ${
          e.message?.toString() || "Unknown error"
        }`,
      );
    }
  };

  const handleDeleteDataSources = async () => {
    const selected = getAllSelectedDataSources();
    if (selected.length === 0) return;

    try {
      for (const ds of selected) {
        await apiClient.request("/admin/delete-datasource", {
          method: "POST",
          data: { source_id: ds.id, source_type: ds.type },
        });
      }
      toast.success(gettext("Data sources deleted successfully"));
      setShowDeleteDatasourceModal(false);
      setSelectedDataSources(new Map());
      fetchStations();
    } catch (e: any) {
      toast.error(
        `${gettext("Error deleting data sources")}: ${
          e.message?.toString() || "Unknown error"
        }`,
      );
    }
  };

  const getSelectedStation = (): Station | undefined => {
    if (selectedStations.size !== 1) return undefined;
    const stationId = Array.from(selectedStations)[0];
    return stationsData?.stations.find((s) => s.id === stationId);
  };

  const getSelectedStationDataSources = (): DataSource[] => {
    const station = getSelectedStation();
    if (!station) return [];
    return [
      ...station.channels,
      ...station.formulas,
      ...station.manual_point_sets,
    ];
  };

  const canDeleteStation = (): boolean => {
    if (selectedStations.size !== 1) return false;
    const station = getSelectedStation();
    if (!station) return false;
    return (
      station.channels.length === 0 &&
      station.formulas.length === 0 &&
      station.manual_point_sets.length === 0
    );
  };

  if (fetchError) {
    console.error("StationManagementPage error: ", fetchError);
    return <SessionTimeout />;
  }

  const stations = stationsData?.stations || [];
  const totalPages = Math.ceil((stationsData?.total || 0) / ITEMS_PER_PAGE);

  const selectedDsCount = getAllSelectedDataSources().length;
  const hasDataSourceSelection = selectedDsCount > 0;
  const hasStationSelection = selectedStations.size > 0;

  return (
    <div className="StationManagementPage">
      <PageHeader
        title={gettext("Station Management")}
        description={gettext(
          "Manage stations, data sources, and perform bulk operations",
        )}
      >
        <div className="d-flex flex-wrap gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCreateModal(true)}
          >
            {gettext("Add station")}
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => setShowDeleteStationModal(true)}
            disabled={!canDeleteStation()}
          >
            {gettext("Delete station")}
          </Button>
        </div>
      </PageHeader>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col xs={12} lg={6}>
              <Form.Label className="fw-semibold">
                {gettext("Search")}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext(
                  "Search by station name, identifier or organization",
                )}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Col>
          </Row>

          {hasDataSourceSelection && (
            <div className="mb-3 p-2 bg-light rounded">
              <small className="text-muted d-block mb-2">
                {gettext("Selected data sources")}: {selectedDsCount}
              </small>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowMoveModal(true)}
                >
                  {gettext("Move to station")}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowMergeModal(true)}
                  disabled={!canMerge()}
                >
                  {gettext("Merge selected")}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowConvertModal(true)}
                  disabled={!canConvert()}
                >
                  {gettext("Convert type")}
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setShowDeleteDatasourceModal(true)}
                >
                  {gettext("Delete")}
                </Button>
              </div>
            </div>
          )}

          <div className="table-responsive">
            <Table className="station-management-table" size="sm">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <span className="visually-hidden">{gettext("Select")}</span>
                  </th>
                  <th style={{ width: "40px" }}>
                    <span className="visually-hidden">{gettext("Expand")}</span>
                  </th>
                  <th>{gettext("Organization")}</th>
                  <th>{gettext("Name")}</th>
                  <th>{gettext("Identifier")}</th>
                  <th>{gettext("Data sources")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      {gettext("Loading...")}
                    </td>
                  </tr>
                ) : stations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      {gettext("No stations found")}
                    </td>
                  </tr>
                ) : (
                  stations.map((station) => {
                    const isExpanded = expandedStations.has(station.id);
                    const isSelected = selectedStations.has(station.id);
                    const dataSourceCount =
                      station.channels.length +
                      station.formulas.length +
                      station.manual_point_sets.length;
                    const stationSelections =
                      selectedDataSources.get(station.id) || new Set();

                    return (
                      <React.Fragment key={station.id}>
                        <tr className={isSelected ? "table-primary" : ""}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleStationSelected(station.id)}
                              aria-label={gettext("Select station")}
                            />
                          </td>
                          <td>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() => toggleStationExpanded(station.id)}
                              disabled={dataSourceCount === 0}
                              aria-label={
                                isExpanded
                                  ? gettext("Collapse")
                                  : gettext("Expand")
                              }
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  transition: "transform 0.2s ease",
                                  transform: isExpanded
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                                }}
                              >
                                ▶
                              </span>
                            </Button>
                          </td>
                          <td>{station.organization_name || "—"}</td>
                          <td>
                            <Link
                              to={`/stations/${station.identifier}`}
                              className="fw-semibold"
                            >
                              {station.name}
                            </Link>
                          </td>
                          <td>{station.identifier}</td>
                          <td>{dataSourceCount}</td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className="p-0">
                              <div className="bg-light p-2">
                                {dataSourceCount === 0 ? (
                                  <p className="text-muted mb-0">
                                    {gettext("No data sources")}
                                  </p>
                                ) : (
                                  <Table size="sm" className="mb-0">
                                    <thead>
                                      <tr>
                                        <th style={{ width: "40px" }}>
                                          <Form.Check
                                            type="checkbox"
                                            checked={areAllDataSourcesSelected(
                                              station,
                                            )}
                                            onChange={() =>
                                              toggleSelectAllDataSources(
                                                station,
                                              )
                                            }
                                            aria-label={gettext(
                                              "Select all data sources",
                                            )}
                                          />
                                        </th>
                                        <th>{gettext("Type")}</th>
                                        <th>{gettext("Name")}</th>
                                        <th>{gettext("Unit")}</th>
                                        <th>{gettext("Details")}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {station.channels.map((ds) => {
                                        const key = `channel:${ds.id}`;
                                        const isDsSelected =
                                          stationSelections.has(key);
                                        return (
                                          <tr
                                            key={key}
                                            className={
                                              isDsSelected
                                                ? "table-secondary"
                                                : ""
                                            }
                                          >
                                            <td>
                                              <Form.Check
                                                type="checkbox"
                                                checked={isDsSelected}
                                                onChange={() =>
                                                  toggleDataSourceSelected(
                                                    station.id,
                                                    key,
                                                  )
                                                }
                                                aria-label={gettext(
                                                  "Select data source",
                                                )}
                                              />
                                            </td>
                                            <td>
                                              <DataSourceTypeBadge
                                                type={ds.type}
                                              />
                                            </td>
                                            <td>{ds.name}</td>
                                            <td>{ds.unit_symbol || "—"}</td>
                                            <td>
                                              <small className="text-muted">
                                                {ds.datapoint_count || 0}{" "}
                                                {gettext("datapoints")}
                                              </small>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                      {station.formulas.map((ds) => {
                                        const key = `formula:${ds.id}`;
                                        const isDsSelected =
                                          stationSelections.has(key);
                                        return (
                                          <tr
                                            key={key}
                                            className={
                                              isDsSelected
                                                ? "table-secondary"
                                                : ""
                                            }
                                          >
                                            <td>
                                              <Form.Check
                                                type="checkbox"
                                                checked={isDsSelected}
                                                onChange={() =>
                                                  toggleDataSourceSelected(
                                                    station.id,
                                                    key,
                                                  )
                                                }
                                                aria-label={gettext(
                                                  "Select data source",
                                                )}
                                              />
                                            </td>
                                            <td>
                                              <DataSourceTypeBadge
                                                type={ds.type}
                                              />
                                            </td>
                                            <td>{ds.name}</td>
                                            <td>{ds.unit_symbol || "—"}</td>
                                            <td>
                                              <small className="text-muted">
                                                {ds.expression}
                                              </small>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                      {station.manual_point_sets.map((ds) => {
                                        const key = `manual_point_set:${ds.id}`;
                                        const isDsSelected =
                                          stationSelections.has(key);
                                        return (
                                          <tr
                                            key={key}
                                            className={
                                              isDsSelected
                                                ? "table-secondary"
                                                : ""
                                            }
                                          >
                                            <td>
                                              <Form.Check
                                                type="checkbox"
                                                checked={isDsSelected}
                                                onChange={() =>
                                                  toggleDataSourceSelected(
                                                    station.id,
                                                    key,
                                                  )
                                                }
                                                aria-label={gettext(
                                                  "Select data source",
                                                )}
                                              />
                                            </td>
                                            <td>
                                              <DataSourceTypeBadge
                                                type={ds.type}
                                              />
                                            </td>
                                            <td>{ds.name}</td>
                                            <td>{ds.unit_symbol || "—"}</td>
                                            <td>
                                              <small className="text-muted">
                                                {ds.datapoint_count || 0}{" "}
                                                {gettext("datapoints")}
                                              </small>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </Table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <Pagination className="justify-content-center mb-0">
              <Pagination.Prev
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {gettext("Previous")}
              </Pagination.Prev>
              <Pagination.Item disabled>
                {gettext("Page")} {currentPage} {gettext("of")} {totalPages}
              </Pagination.Item>
              <Pagination.Next
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
              >
                {gettext("Next")}
              </Pagination.Next>
            </Pagination>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{gettext("Add station")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Identifier")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext("Enter station identifier")}
                value={newStationIdentifier}
                onChange={(e) => setNewStationIdentifier(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Name")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext("Enter station name")}
                value={newStationName}
                onChange={(e) => setNewStationName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Organization (optional)")}</Form.Label>
              <Form.Select
                value={newStationOrgId}
                onChange={(e) =>
                  setNewStationOrgId(
                    e.target.value ? parseInt(e.target.value, 10) : "",
                  )
                }
              >
                <option value="">{gettext("No organization")}</option>
                {organizationsData?.organization?.map((org: any) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowCreateModal(false)}
          >
            {gettext("Cancel")}
          </Button>
          <Button variant="primary" onClick={handleCreateStation}>
            {gettext("Create")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteStationModal}
        onHide={() => setShowDeleteStationModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{gettext("Delete station")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {gettext(
              "Are you sure you want to delete this station? This action cannot be undone.",
            )}
          </p>
          <p className="fw-bold">{getSelectedStation()?.name}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteStationModal(false)}
          >
            {gettext("Cancel")}
          </Button>
          <Button variant="danger" onClick={handleDeleteStation}>
            {gettext("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMoveModal}
        onHide={() => setShowMoveModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{gettext("Move data sources")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {gettext(
              "Select a target station to move the selected data sources to:",
            )}
          </p>
          <SearchableDropdown
            placeholder={gettext("Select station")}
            value={targetStationId ? String(targetStationId) : ""}
            options={(allStationsData?.station || [])
              .filter((s: any) => !selectedStations.has(s.id))
              .map((station: any) => ({
                value: String(station.id),
                label: `${station.name} (${station.identifier})`,
              }))}
            onChange={(value) =>
              setTargetStationId(value ? parseInt(value, 10) : "")
            }
            useFixedPositioning={true}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowMoveModal(false)}
          >
            {gettext("Cancel")}
          </Button>
          <Button variant="primary" onClick={handleMoveDataSources}>
            {gettext("Move")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMergeModal}
        onHide={() => !isMerging && setShowMergeModal(false)}
        centered
        backdrop={isMerging ? "static" : true}
      >
        <Modal.Header closeButton={!isMerging}>
          <Modal.Title>{gettext("Merge data sources")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {gettext(
              "Select a target data source to merge all selected data into. Data from the other selected sources will be moved to the target, and their formulas, alarms, annotations, charts and dashboards will be updated to use the target. The other selected sources will then be deleted.",
            )}
          </p>
          <Form.Select
            value={targetDataSourceId}
            onChange={(e) =>
              setTargetDataSourceId(
                e.target.value ? parseInt(e.target.value, 10) : "",
              )
            }
          >
            <option value="">{gettext("Select target")}</option>
            {getAllSelectedDataSources().map((ds) => (
              <option key={ds.id} value={ds.id}>
                {ds.name} ({ds.type})
              </option>
            ))}
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowMergeModal(false)}
            disabled={isMerging}
          >
            {gettext("Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleMergeDataSources}
            disabled={isMerging}
          >
            {isMerging && (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                className="me-2"
              />
            )}
            {isMerging ? gettext("Merging...") : gettext("Merge")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConvertModal}
        onHide={() => setShowConvertModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{gettext("Convert data source")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {getSelectedDataSourceTypes().has("channel") && (
            <p>
              {gettext(
                "Convert the selected channel to a manual point set? All datapoints will be preserved. The original channel will be deleted.",
              )}
            </p>
          )}
          {getSelectedDataSourceTypes().has("manual_point_set") && (
            <p>
              {gettext(
                "Convert the selected manual point set to a channel? All datapoints will be preserved. The original manual point set will be deleted.",
              )}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowConvertModal(false)}
          >
            {gettext("Cancel")}
          </Button>
          <Button variant="primary" onClick={handleConvert}>
            {gettext("Convert")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteDatasourceModal}
        onHide={() => setShowDeleteDatasourceModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{gettext("Delete data sources")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {gettext(
              "Are you sure you want to delete the selected data sources? This will also delete all underlying datapoints. This action cannot be undone.",
            )}
          </p>
          <p className="fw-bold">
            {selectedDsCount} {gettext("data sources selected")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteDatasourceModal(false)}
          >
            {gettext("Cancel")}
          </Button>
          <Button variant="danger" onClick={handleDeleteDataSources}>
            {gettext("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StationManagementPage;
